import { useEffect, useRef, useState } from 'react';
import {
	Modal,
	Form,
	Input,
	Button,
	Select,
	InputNumber,
	Row,
	Col,
	notification,
	Splitter,
	Table,
	Alert,
	Tabs,
	Typography,
	Empty,
	Tooltip,
	Spin,
	Checkbox,
	Tag,
} from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import {
	useGetMaterialVendorsQuery,
	useGetMaterialShapesQuery,
	useGetMaterialTypesQuery,
	useGetMaterialCategoriesQuery,
	useCalculateRequiredLengthsMutation,
	useCalculateScrapQuantityMutation,
} from '../../redux/api/material';
import { useGetBuildingsQuery } from '../../redux/api/location';
import {
	ANGLE,
	CHANNEL,
	PLATE,
	RECTANGLE_BAR,
	RECTANGLE_TUBE,
	ROUND_BAR,
	ROUND_TUBE,
	HEX_BAR,
} from '../../config/constants';
import { closeMaterialRequestModal } from '../../redux/slices/material-request';
import { showAddVendorMaterialModal } from '../../redux/slices/material';
import {
	useAddMaterialRequestMutation,
	useUpdateMaterialRequestMutation,
} from '../../redux/api/material-request';
import { Lineitem, ProductionMethod } from '../../interfaces/Ordel.Interface';
import ThreeDStockSizePreviewer from '../ThreeDStockPreviewer/lazy.tsx';
import {
	useGetUniqueVendorMaterialHeightsQuery,
	useGetUniqueVendorMaterialInnerDiametersQuery,
	useGetUniqueVendorMaterialOuterDiametersQuery,
	useGetUniqueVendorMaterialWallThicknessesQuery,
	useGetUniqueVendorMaterialWidthsQuery,
	useGetVendorMaterialsQuery,
} from '../../redux/api/vendor-materials';
import {
	IVendorMaterial,
	IVendorMaterialLength,
	MaterialRequest,
	MaterialShape,
} from '../../interfaces/Material.Interface';
import {
	useAlignPartByFaceMutation,
	useAlignPartToXAxisByFaceMutation,
	useGetStockPreviewByDimensionsQuery,
	useGetSuggestedVendorMaterialBasedOnPartOrientationQuery,
	useRotatePartMutation,
} from '../../redux/api/order-line-item';
import {
	LoadingOutlined,
	PlusOutlined,
	QuestionCircleTwoTone,
	WarningOutlined,
} from '@ant-design/icons';
import { RootState } from '../../redux/store';
import VendorMaterialModal from '../VendorMaterialModal';
import { ScrapBreakdown } from '../ScrapInfo/ScrapInfoButton';
import ScrapRecommendation from '../ScrapInfo/ScrapRecommendation';
import { DEFAULT_SAW_KERF, DEFAULT_TRIM_CUT_LENGTH, SWISS_SAW_KERF } from '../../utils';
import { getEffectiveShapeName } from '../../utils/shapeUtils';

const computeVendorMaterialSize = (record: IVendorMaterial): string => {
	if (record.display_size) return record.display_size;
	const effectiveShape = getEffectiveShapeName(record.shape?.display_name);
	const h = record.height ?? '';
	const w = record.width ?? '';
	const od = record.outer_diameter ?? '';
	const id = record.inner_diameter ?? '';
	const wt = record.wall_thickness ?? '';
	if (effectiveShape === RECTANGLE_BAR || effectiveShape === PLATE || effectiveShape === HEX_BAR) {
		return `${h}" H x ${w}" W`;
	}
	if (effectiveShape === RECTANGLE_TUBE || effectiveShape === ANGLE || effectiveShape === CHANNEL) {
		return `${h}" H x ${w}" W x ${wt}" WT`;
	}
	if (effectiveShape === ROUND_BAR) {
		return `${od}" OD`;
	}
	if (effectiveShape === ROUND_TUBE) {
		return `${od}" OD x ${id}" ID`;
	}
	return '';
};

const { Text, Title } = Typography;

interface MaterialModalProps {
	lineItem: Lineitem;
	materialRequest?: MaterialRequest | null;
	refetchLineItem?: () => void;
	refetchData: () => void;
	onClose?: () => void;
}

const MaterialRequestModal = (props: MaterialModalProps) => {
	const dispatch = useDispatch();

	const visible = useSelector(
		(state: RootState) => state.materialRequest.materialRequestModalVisible
	);

	const [form] = Form.useForm();

	const isSwissProduction =
		props?.lineItem?.partsbadger_production_method === ProductionMethod.Swiss;

	const [addMaterialRequst, addMaterialRequestResult] =
		useAddMaterialRequestMutation();
	const [updateMaterialRequest, updateMaterialRequestResult] =
		useUpdateMaterialRequestMutation();

	const [loading, setLoading] = useState(false);

	const [isStockRotated, setIsStockRotated] = useState(false);
	const [allowRotatedStock, setAllowRotatedStock] = useState(false);

	const [selectedShape, setSelectedShape] = useState<MaterialShape | null>(
		null
	);
	const normalizeShape = (
		shape: MaterialShape | null | undefined
	): MaterialShape | null => {
		if (!shape) return null;
		return {
			...shape,
			display_name:
				getEffectiveShapeName(shape.display_name) ?? shape.display_name,
		};
	};
	const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
	const [selectedTypes, setSelectedTypes] = useState<string | string[] | []>(
		[]
	);
	const [selectedHeight, setSelectedHeight] = useState<string | null>(null);
	const [selectedWidth, setSelectedWidth] = useState<string | null>(null);
	const [plateCutWidth, setPlateCutWidth] = useState<number | null>(null);
	const [selectedOuterDiameter, setSelectedOuterDiameter] = useState<
		string | null | number
	>(null);
	const [selectedInnerDiameter, setSelectedInnerDiameter] = useState<
		string | null | number
	>(null);
	const [selectedWallThickness, setSelectedWallThickness] = useState<
		string | null | number
	>(null);
	const [materialCutLength, setMaterialCutLength] = useState<string | null>(
		null
	);
	const [autoCalculatedPartLength, setAutoCalculatedPartLength] = useState<
		string | null
	>(null);
	const [sawKerf, setSawKerf] = useState<number>(
		isSwissProduction ? SWISS_SAW_KERF : DEFAULT_SAW_KERF
	);
	const [trimCutLength, setTrimCutLength] = useState<number>(DEFAULT_TRIM_CUT_LENGTH);
	const [cutQuantity, setCutQuantity] = useState<number>(
		props?.lineItem?.quantity
	);
	// Default: 10 pcs + 3% of Parts (Swiss Production only)
	const [estimatedScrapQuantity, setEstimatedScrapQuantity] = useState<number>(
		isSwissProduction
			? 10 + Math.round(props?.lineItem?.quantity * 0.03)
			: Math.round(props?.lineItem?.quantity * 0.15)
	);
	const [totalRequiredLength, setTotalRequiredLength] = useState<number>(0);
	const [numberOfBars, setNumberOfBars] = useState<number>(0);

	// Breakdown of the standard-production scrap lookup, shown in the info modal.
	const [scrapBreakdown, setScrapBreakdown] =
		useState<ScrapBreakdown | null>(null);

	const [selectedFullBarLength, setSelectedFullBarLength] =
		useState<IVendorMaterialLength>();
	const [selectedVendorMaterial, setSelectedVendorMaterial] =
		useState<IVendorMaterial | null>();

	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(20);

	const vendorsQuery = useGetMaterialVendorsQuery();
	const shapesQuery = useGetMaterialShapesQuery();
	const categoriesQuery = useGetMaterialCategoriesQuery();
	const { data: buildings = [], isLoading: buildingsLoading } = useGetBuildingsQuery();
	const lockedBuilding = props.lineItem?.locked_building ?? null;
	const defaultBuildingId = lockedBuilding?.id ?? (buildings.length === 1 ? buildings[0].id : undefined);
	const typesQuery = useGetMaterialTypesQuery(
		{ material_category: selectedCategory },
		{ refetchOnMountOrArgChange: true }
	);

	const vendorMaterialsQuery = useGetVendorMaterialsQuery(
		{
			shape: selectedShape?.id,
			category: selectedCategory,
			types: selectedTypes,
			...(selectedHeight ? { height: selectedHeight } : null),
			...(selectedOuterDiameter
				? { outer_diameter: selectedOuterDiameter }
				: null),
			...(selectedWidth ? { width: selectedWidth } : null),
			page: currentPage,
		},
		{
			skip: !selectedShape || !selectedCategory || selectedTypes.length === 0,
			refetchOnMountOrArgChange: true,
		}
	);

	const uniqueHeightsQuery = useGetUniqueVendorMaterialHeightsQuery(
		{
			shape: selectedShape?.id,
			...(selectedCategory ? { category: selectedCategory } : null),
			...(selectedTypes ? { types: selectedTypes } : null),
		},
		{
			skip: !selectedShape,
			refetchOnMountOrArgChange: true,
		}
	);

	const uniqueWidthsQuery = useGetUniqueVendorMaterialWidthsQuery(
		{
			shape: selectedShape?.id,
			...(selectedCategory ? { category: selectedCategory } : null),
			...(selectedTypes ? { types: selectedTypes } : null),
			...(selectedHeight ? { height: selectedHeight } : null),
		},
		{
			skip: !selectedShape || !selectedHeight,
			refetchOnMountOrArgChange: true,
		}
	);
	const uniqueOuterDiametersQuery =
		useGetUniqueVendorMaterialOuterDiametersQuery(
			{
				shape: selectedShape?.id,
				...(selectedCategory ? { category: selectedCategory } : null),
				...(selectedTypes ? { types: selectedTypes } : null),
			},
			{
				skip: !selectedShape,
				refetchOnMountOrArgChange: true,
			}
		);
	const uniqueInnerDiametersQuery =
		useGetUniqueVendorMaterialInnerDiametersQuery(
			{
				shape: selectedShape?.id,
				...(selectedCategory ? { category: selectedCategory } : null),
				...(selectedTypes ? { types: selectedTypes } : null),
				...(selectedOuterDiameter
					? { outer_diameter: selectedOuterDiameter }
					: null),
			},
			{
				skip: !selectedShape,
				refetchOnMountOrArgChange: true,
			}
		);
	const uniqueWallThicknessesQuery =
		useGetUniqueVendorMaterialWallThicknessesQuery(
			{
				shape: selectedShape?.id,
				...(selectedCategory ? { category: selectedCategory } : null),
				...(selectedTypes ? { types: selectedTypes } : null),
				...(selectedHeight ? { height: selectedHeight } : null),
				...(selectedWidth ? { width: selectedWidth } : null),
			},
			{
				skip: !selectedShape,
				refetchOnMountOrArgChange: true,
			}
		);

	const getSuggestedVendorMaterialBasedOnPartOrientationQuery =
		useGetSuggestedVendorMaterialBasedOnPartOrientationQuery(
			{
				id: props?.lineItem?.id,
				shape: selectedShape?.id,
				types: selectedTypes,
				allow_rotated_stock: allowRotatedStock,
			},
			{
				skip:
					Boolean(props.materialRequest) ||
					!props?.lineItem ||
					!selectedShape ||
					selectedTypes.length === 0 ||
					(selectedShape?.display_name !== RECTANGLE_BAR &&
						selectedShape?.display_name !== ROUND_BAR),
				refetchOnMountOrArgChange: true,
			}
		);

	const stockPreviewQuery = useGetStockPreviewByDimensionsQuery(
		{
			id: props?.lineItem?.id,
			params: {
				...(selectedShape ? { shape: selectedShape.id } : null),
				...(selectedHeight ? { height: selectedHeight } : null),
				...(selectedWidth != null || plateCutWidth != null
					? {
						width:
							selectedShape?.display_name === PLATE
								? plateCutWidth
								: selectedWidth,
					}
					: null),
				...(selectedOuterDiameter != null
					? { outer_diameter: selectedOuterDiameter }
					: null),
				...(selectedInnerDiameter != null
					? { inner_diameter: selectedInnerDiameter }
					: null),
				...(selectedWallThickness != null
					? { wall_thickness: selectedWallThickness }
					: null),
				...(materialCutLength != null ? { length: materialCutLength } : null),
				is_stock_rotated: isStockRotated,
				stock_facing_amount: 0.03,
			},
		},
		{
			skip: !visible || !props?.lineItem?.step_file,
			// || !selectedShape |
			// !props?.lineItem?.id ||
			// || !materialCutLength,
			refetchOnMountOrArgChange: true,
		}
	);

	const [rotatePart, rotatePartResult] = useRotatePartMutation();
	const [alignByFace, alignByFaceResult] = useAlignPartByFaceMutation();
	const [alignToXAxisByFace, alignToXAxisByFaceResult] =
		useAlignPartToXAxisByFaceMutation();

	const [calculateRequiredLengths, calculateRequiredLengthsResult] =
		useCalculateRequiredLengthsMutation();

	const [calculateScrapQuantity] = useCalculateScrapQuantityMutation();
	const latestScrapRequest = useRef(0);

	// Resolve the material type id used to look up the default cost-per-cubic-inch.
	const getSelectedTypeId = (): string | null => {
		if (selectedVendorMaterial?.type?.id) {
			return selectedVendorMaterial.type.id;
		}
		if (Array.isArray(selectedTypes)) {
			return selectedTypes.length === 1 ? String(selectedTypes[0]) : null;
		}
		return selectedTypes ? String(selectedTypes) : null;
	};

	/**
	 * Standard production only (PTB-5965): suggest the scrap quantity from the
	 * cost/volume lookup table. Falls back to 15% of quantity when the backend
	 * can't determine a cost per piece. Updates the field (still editable) and,
	 * when requested, recalculates the total required length.
	 */
	const recomputeStandardScrap = async (params: {
		quantity: number;
		materialCutLength?: string | number | null;
		shape?: string | null;
		height?: string | number | null;
		width?: string | number | null;
		outer_diameter?: string | number | null;
		inner_diameter?: string | number | null;
		wall_thickness?: string | number | null;
		materialTypeId?: string | null;
		recalcLength?: boolean;
		// When false, only refresh the recommendation hint (scrapBreakdown)
		// without overwriting the editable field or the total required length.
		// Used when opening the edit modal so the saved value is preserved.
		updateField?: boolean;
	}): Promise<number> => {
		const requestId = ++latestScrapRequest.current;
		const { quantity } = params;
		// Scrap can legitimately be 0: the backend lookup table (PTB-5965)
		// intentionally recommends 0 extra pieces for expensive / low-volume
		// jobs, so preserve 0 instead of clamping up to 1.
		const fallback =
			quantity && quantity >= 1 ? Math.max(Math.round(quantity * 0.15), 1) : 0;
		let scrap = fallback;

		if (quantity && quantity >= 1) {
			try {
				const res = await calculateScrapQuantity({
					quantity,
					shape: params.shape ?? null,
					material_cut_length: params.materialCutLength
						? Number(params.materialCutLength)
						: null,
					height: params.height ?? null,
					width: params.width ?? null,
					outer_diameter: params.outer_diameter ?? null,
					inner_diameter: params.inner_diameter ?? null,
					wall_thickness: params.wall_thickness ?? null,
					material_type_id:
						params.materialTypeId ?? getSelectedTypeId(),
				}).unwrap();
				if (requestId !== latestScrapRequest.current) {
					return scrap;
				}
				scrap = res?.estimated_scrap_quantity ?? fallback;
				setScrapBreakdown({ ...(res as ScrapBreakdown), manual: false });
			} catch {
				if (requestId !== latestScrapRequest.current) {
					return scrap;
				}
				scrap = fallback;
				setScrapBreakdown({
					quantity,
					cost_per_piece: null,
					estimated_scrap_quantity: fallback,
					used_fallback: true,
					fallback_reason: 'cost_per_piece_unavailable',
					fallback_percent: 0.15,
					quantity_tier: null,
					cost_tier: null,
					manual: false,
				});
			}
		}

		scrap = Math.max(Number(scrap) || 0, 0);
		if (params.updateField !== false) {
			setEstimatedScrapQuantity(scrap);
			form.setFieldValue('estimated_scrap_quantity', scrap);

			const cutLen = params.materialCutLength;
			if (params.recalcLength && cutLen) {
				const data = await calculateRequiredLengths({
					material_cut_length: Number(cutLen),
					saw_kerf: sawKerf,
					material_cut_quantity: scrap + quantity,
					required_trim_cut_length: trimCutLength,
				}).unwrap();
				if (requestId !== latestScrapRequest.current) {
					return scrap;
				}
				setTotalRequiredLength(data.total_required_length);
				form.setFieldValue(
					'total_required_length',
					data.total_required_length
				);
			}
		}

		return scrap;
	};

	async function handleResetPartRotation() {
		await rotatePart({
			id: props?.lineItem?.id,
			body: {
				part_transform_matrix: [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1],
			},
		})
			.unwrap()
			.then(() => {
				if (props.refetchLineItem) {
					props.refetchLineItem();
				}
			});
	}

	async function handleClickFaceForAlignment(faceId: number) {
		await alignByFace({
			id: props?.lineItem?.id,
			body: {
				id: faceId,
			},
		})
			.unwrap()
			.then(() => {
				if (props.refetchLineItem) {
					props.refetchLineItem();
				}
			});
	}

	async function handleClickFaceForXAxisAlignment(faceId: number) {
		await alignToXAxisByFace({
			id: props?.lineItem?.id,
			body: {
				id: faceId,
			},
		})
			.unwrap()
			.then(() => {
				if (props.refetchLineItem) {
					props.refetchLineItem();
				}
			});
	}

	async function updateMatrix(matrix: number[]) {
		await rotatePart({
			id: props?.lineItem?.id,
			body: {
				part_transform_matrix: matrix,
			},
		})
			.unwrap()
			.then(() => {
				if (props.refetchLineItem) {
					props.refetchLineItem();
				}
			});
	}

	function onClose() {
		if (props.onClose) {
			props.onClose();
		}
		dispatch(closeMaterialRequestModal());
		setSelectedShape(null);
		setSelectedCategory(null);
		setSelectedTypes([]);
		setSelectedHeight(null);
		setSelectedWidth(null);
		setSelectedOuterDiameter(null);
		setSelectedInnerDiameter(null);
		setSelectedWallThickness(null);
		setMaterialCutLength(null);
		setAutoCalculatedPartLength(null);
		setPlateCutWidth(null);
		setTotalRequiredLength(0);
		setNumberOfBars(0);
		setSawKerf(isSwissProduction ? SWISS_SAW_KERF : DEFAULT_SAW_KERF);
		setCutQuantity(props?.lineItem?.quantity);
		setEstimatedScrapQuantity(
			isSwissProduction
				? 10 + Math.round(props?.lineItem?.quantity * 0.03)
				: Math.round(props?.lineItem?.quantity * 0.15)
		);
		setSelectedVendorMaterial(null);
		setSelectedRowKeys([]);
		setIsStockRotated(false);
		setAllowRotatedStock(false);
		form.resetFields();
	}

	interface IMaterialFormProps {
		vendor_material: string;
		plate_cut_width: number;
		material_cut_length: number;
		material_cut_quantity: number;
		estimated_scrap_quantity: number;
		total_required_length: number;
		is_stock_rotated: boolean;
		notes: string;
		saw_kerf: number;
		building?: string;
	}

	const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

	const selectRow = (record: IVendorMaterial) => {
		setSelectedRowKeys([record.id]);
		form.setFieldsValue({
			vendor_material: record.id,
		});
		const vendorMaterial = vendorMaterialsQuery?.data?.results?.find(
			(material) => material.id == record.id
		);

		if (vendorMaterial) {
			setSelectedVendorMaterial(vendorMaterial);
			form.setFieldValue('vendor_material', vendorMaterial.id);

			if (
				selectedShape?.display_name === RECTANGLE_BAR ||
				selectedShape?.display_name === RECTANGLE_TUBE ||
				selectedShape?.display_name === PLATE ||
				selectedShape?.display_name === ANGLE ||
				selectedShape?.display_name === CHANNEL ||
				selectedShape?.display_name === HEX_BAR
			) {
				setSelectedHeight(Number(vendorMaterial.height));
				form.setFieldValue('height', Number(vendorMaterial.height));
			}
			if (
				selectedShape?.display_name === RECTANGLE_BAR ||
				selectedShape?.display_name === RECTANGLE_TUBE ||
				selectedShape?.display_name === ANGLE ||
				selectedShape?.display_name === CHANNEL
			) {
				setSelectedWidth(Number(vendorMaterial.width));
				form.setFieldValue('width', Number(vendorMaterial.width));
			}
			if (
				selectedShape?.display_name === ROUND_BAR ||
				selectedShape?.display_name === ROUND_TUBE
			) {
				setSelectedOuterDiameter(Number(vendorMaterial.outer_diameter));
				form.setFieldValue(
					'outer_diameter',
					Number(vendorMaterial.outer_diameter)
				);
			}

			if (
				selectedShape?.display_name === RECTANGLE_TUBE ||
				selectedShape?.display_name === ANGLE ||
				selectedShape?.display_name === CHANNEL
			) {
				setSelectedWallThickness(vendorMaterial?.wall_thickness);
				form.setFieldValue(
					'wall_thickness',
					Number(vendorMaterial.wall_thickness)
				);
			}

			if (selectedShape?.display_name === ROUND_TUBE) {
				setSelectedInnerDiameter(vendorMaterial?.inner_diameter);
				form.setFieldValue(
					'inner_diameter',
					Number(vendorMaterial.inner_diameter)
				);
			}

			if (!isSwissProduction) {
				recomputeStandardScrap({
					quantity: cutQuantity,
					materialCutLength,
					shape: selectedShape?.display_name,
					height: vendorMaterial.height,
					width: vendorMaterial.width,
					outer_diameter: vendorMaterial.outer_diameter,
					inner_diameter: vendorMaterial.inner_diameter,
					wall_thickness: vendorMaterial.wall_thickness,
					materialTypeId:
						vendorMaterial.type?.id ?? getSelectedTypeId(),
					recalcLength: true,
				});
			}
		}
	};

	const onSelectedRowKeysChange = (newSelectedRowKeys: React.Key[]) => {
		const record = vendorMaterialsQuery?.data?.results?.find(
			(material) => material.id === newSelectedRowKeys[0]?.toLocaleString()
		);

		if (record) {
			selectRow(record);
		}
	};

	const handleOk = async (values: IMaterialFormProps) => {
		try {
			setLoading(true);

		const payload = {
			order_line_item: props?.lineItem?.id,
			plate_cut_width: values.plate_cut_width,
			material_cut_length: values.material_cut_length,
			material_cut_quantity: values.material_cut_quantity,
			estimated_scrap_quantity: values.estimated_scrap_quantity,
			total_required_length: values.total_required_length,
			is_stock_rotated:
				getSuggestedVendorMaterialBasedOnPartOrientationQuery?.data
					?.is_stock_rotated,
			vendor_material: values.vendor_material,
			notes: values.notes,
			saw_kerf: values.saw_kerf,
			required_trim_cut_length: isSwissProduction ? null : (values.required_trim_cut_length ?? trimCutLength),
			building: values.building ?? defaultBuildingId ?? null,
		};

			if (props.materialRequest) {
				updateMaterialRequest({
					id: props.materialRequest.id,
					values: payload,
				})
					.unwrap()
					.then(() => {
						notification.success({
							message: 'Material Request Updated Successfully',
						});
						props.refetchData();
					});
			} else if (payload) {
				addMaterialRequst(payload)
					.unwrap()
					.then(() => {
						onClose();
						notification.success({
							message: 'Material Request Created Successfully',
							placement: 'topRight',
						});
						props.refetchData();
					});
			}
		} catch (error) {
			notification.error({
				message: 'An error occurred',
				placement: 'topRight',
			});
		} finally {
			setLoading(false);
		}
	};

	const handleCancel = () => {
		form.resetFields();
		onClose();
	};

	// function calculateRequiredMaterialLength(
	// 	materialCutLength: number,
	// 	sawKerf: number,
	// 	materialCutQuantity: number
	// ) {
	// 	return Number(
	// 		Number((materialCutLength + sawKerf) * materialCutQuantity).toFixed(2)
	// 	);
	// }

	// Handle shape change
	const handleShapeChange = (value: string) => {
		const shape = shapesQuery?.data?.find((x) => x.id === value);

		if (shape) {
			setSelectedShape(normalizeShape(shape));
		}

		form.resetFields(['vendor_material']);

		setSelectedTypes([]);
		setSelectedHeight(null);
		setSelectedWidth(null);
		setSelectedOuterDiameter(null);
		setSelectedInnerDiameter(null);
		setSelectedWallThickness(null);
		setSelectedVendorMaterial(null);
		setTotalRequiredLength(0);
	};

	// Handle shape change
	const handleCategoryChange = (value: string) => {
		setSelectedCategory(value);
	};

	const handlePlateCutWidthChange = (value: string) => {
		if (value !== plateCutWidth) {
			setPlateCutWidth(value);
		}
	};

	// Swiss-specific calculation: Total Inches Needed = ((Part Length + Cut Off Length) * (Parts + Est. Scrap)) * (12/11)
	// Number of Bars = Total Inches Needed / 144 (rounded up)
	const calculateSwissRequiredLength = (
		partLength: number,
		cutOffLength: number,
		parts: number,
		estimatedScrap: number
	) => {
		const totalInchesNeeded =
			((partLength + cutOffLength) * (parts + estimatedScrap)) * (12 / 11);
		const roundedTotalInchesNeeded = Number(totalInchesNeeded.toFixed(2));
		const barsNeeded = Math.ceil(totalInchesNeeded / 144);
		return { totalInchesNeeded: roundedTotalInchesNeeded, barsNeeded };
	};

	const handleMaterialCutLengthChange = (value: string | number | null) => {
		// Update form to keep in sync
		form.setFieldValue('material_cut_length', value);

		if (value === null || value === '' || value === undefined) {
			setMaterialCutLength(null);
			if (isSwissProduction) {
				setTotalRequiredLength(0);
				setNumberOfBars(0);
				form.setFieldValue('total_required_length', 0);
				form.setFieldValue('number_of_bars_needed', 0);
			}
			return;
		}

		const stringValue = String(value);

		if (stringValue !== materialCutLength) {
			setMaterialCutLength(stringValue);
			if (isSwissProduction) {
				const result = calculateSwissRequiredLength(
					Number(stringValue),
					sawKerf,
					cutQuantity,
					estimatedScrapQuantity
				);
				setTotalRequiredLength(result.totalInchesNeeded);
				setNumberOfBars(result.barsNeeded);
				form.setFieldValue('total_required_length', result.totalInchesNeeded);
				form.setFieldValue('number_of_bars_needed', result.barsNeeded);
			} else if (!isSwissProduction) {
				recomputeStandardScrap({
					quantity: cutQuantity,
					materialCutLength: stringValue,
					shape: selectedShape?.display_name,
					height: selectedHeight,
					width: selectedWidth,
					outer_diameter: selectedOuterDiameter,
					inner_diameter: selectedInnerDiameter,
					wall_thickness: selectedWallThickness,
					recalcLength: true,
				});
			}
		}
	};

	const handleSawKerfChange = (value: number) => {
		setSawKerf(value);
		if (isSwissProduction && materialCutLength) {
			const result = calculateSwissRequiredLength(
				Number(materialCutLength),
				value,
				cutQuantity,
				estimatedScrapQuantity
			);
			setTotalRequiredLength(result.totalInchesNeeded);
			setNumberOfBars(result.barsNeeded);
			form.setFieldValue('total_required_length', result.totalInchesNeeded);
			form.setFieldValue('number_of_bars_needed', result.barsNeeded);
	} else if (!isSwissProduction && materialCutLength) {
		calculateRequiredLengths({
			material_cut_length: Number(materialCutLength),
			saw_kerf: value,
			material_cut_quantity: estimatedScrapQuantity + cutQuantity,
			required_trim_cut_length: trimCutLength,
		})
			.unwrap()
			.then((data) => {
				setTotalRequiredLength(data.total_required_length);
				form.setFieldValue('total_required_length', data.total_required_length);
			});
	}
};

const handleMaterialCutQuantityChange = (value: number) => {
		setCutQuantity(value);

		if (isSwissProduction) {
			const newEstimatedScrapQuantity = 10 + Math.round(value * 0.03);
			setEstimatedScrapQuantity(newEstimatedScrapQuantity);
			form.setFieldValue(
				'estimated_scrap_quantity',
				newEstimatedScrapQuantity
			);
			if (materialCutLength) {
				const result = calculateSwissRequiredLength(
					Number(materialCutLength),
					sawKerf,
					value,
					newEstimatedScrapQuantity
				);
				setTotalRequiredLength(result.totalInchesNeeded);
				setNumberOfBars(result.barsNeeded);
				form.setFieldValue(
					'total_required_length',
					result.totalInchesNeeded
				);
				form.setFieldValue('number_of_bars_needed', result.barsNeeded);
			}
			return;
		}

		recomputeStandardScrap({
			quantity: value,
			materialCutLength,
			shape: selectedShape?.display_name,
			height: selectedHeight,
			width: selectedWidth,
			outer_diameter: selectedOuterDiameter,
			inner_diameter: selectedInnerDiameter,
			wall_thickness: selectedWallThickness,
			recalcLength: true,
		});
	};

	const handleMaterialScrapQuantityChange = (value: number) => {
		// The scrap field is editable; the recommendation shown beside it
		// (scrapBreakdown) is intentionally left untouched.
		setEstimatedScrapQuantity(value);
		form.setFieldValue('estimated_scrap_quantity', value);
		if (isSwissProduction && materialCutLength) {
			const result = calculateSwissRequiredLength(
				Number(materialCutLength),
				sawKerf,
				cutQuantity,
				value
			);
			setTotalRequiredLength(result.totalInchesNeeded);
			setNumberOfBars(result.barsNeeded);
			form.setFieldValue('total_required_length', result.totalInchesNeeded);
			form.setFieldValue('number_of_bars_needed', result.barsNeeded);
		} else if (!isSwissProduction && materialCutLength) {
			calculateRequiredLengths({
				material_cut_length: Number(materialCutLength),
				saw_kerf: sawKerf,
				material_cut_quantity: cutQuantity + value,
				required_trim_cut_length: trimCutLength,
			})
				.unwrap()
				.then((data) => {
					setTotalRequiredLength(data.total_required_length);
					form.setFieldValue('total_required_length', data.total_required_length);
				});
		}
	};

	function onClickAutoSelectMaterialSize() {
		if (selectedShape?.display_name === RECTANGLE_BAR) {
			setSelectedHeight(
				getSuggestedVendorMaterialBasedOnPartOrientationQuery?.data
					?.vendor_material?.height
			);

			setSelectedWidth(
				getSuggestedVendorMaterialBasedOnPartOrientationQuery?.data
					?.vendor_material?.width
			);
		} else if (selectedShape?.display_name === ROUND_BAR) {
			setSelectedOuterDiameter(
				getSuggestedVendorMaterialBasedOnPartOrientationQuery?.data
					?.vendor_material?.outer_diameter
			);
		}
		const rawValue =
			getSuggestedVendorMaterialBasedOnPartOrientationQuery?.data
				?.material_cut_length;
		// Round to 2 decimal places
		const autoCalculatedValue = rawValue
			? String(Number(Number(rawValue).toFixed(2)))
			: null;
		setAutoCalculatedPartLength(autoCalculatedValue);
		setMaterialCutLength(autoCalculatedValue);
		if (autoCalculatedValue) {
			form.setFieldValue('material_cut_length', Number(autoCalculatedValue));
			handleMaterialCutLengthChange(autoCalculatedValue);
		}
		setSelectedRowKeys([]);
		form.resetFields(['vendor_material']);
	}

	const handleResetPartLength = () => {
		if (autoCalculatedPartLength) {
			const roundedValue = String(
				Number(Number(autoCalculatedPartLength).toFixed(2))
			);
			setMaterialCutLength(roundedValue);
			form.setFieldValue('material_cut_length', Number(roundedValue));
			handleMaterialCutLengthChange(roundedValue);
		}
	};

	// Populate form with existing vendor material data if passed in
	useEffect(() => {
		if (props.materialRequest && visible) {
			// Set cut quantity and estimated scrap quantity
			setCutQuantity(props.materialRequest.material_cut_quantity);
			setEstimatedScrapQuantity(props.materialRequest.estimated_scrap_quantity);

			// Calculate Number of Bars Needed for Swiss production
			if (isSwissProduction && props.materialRequest.total_required_length) {
				const barsNeeded = Math.ceil(Number(props.materialRequest.total_required_length) / 144);
				setNumberOfBars(barsNeeded);
			}

			// Round material_cut_length to 2 decimal places
			const roundedCutLength = props.materialRequest.material_cut_length
				? Number(Number(props.materialRequest.material_cut_length).toFixed(2))
				: null;

		form.setFieldsValue({
			plate_cut_width: props.materialRequest.plate_cut_width,
			material_cut_length: roundedCutLength,
			material_cut_quantity: props.materialRequest.material_cut_quantity,
			estimated_scrap_quantity:
				props.materialRequest.estimated_scrap_quantity,
			total_required_length: props.materialRequest.total_required_length,
			vendor_material: props.materialRequest.vendor_material,
			vendor_part_number: props.materialRequest.vendor_part_number,
			is_stock_rotated: props.materialRequest.is_stock_rotated,
			notes: props.materialRequest.notes,
			saw_kerf: props.materialRequest.saw_kerf ?? (isSwissProduction ? SWISS_SAW_KERF : DEFAULT_SAW_KERF),
			required_trim_cut_length: props.materialRequest.required_trim_cut_length ?? DEFAULT_TRIM_CUT_LENGTH,
			number_of_bars_needed: isSwissProduction
				? Math.ceil(Number(props.materialRequest.total_required_length) / 144)
				: 0,
			building: props.materialRequest.building?.id ?? defaultBuildingId,
		});
			setSelectedShape(
				normalizeShape(props.materialRequest.vendor_material.shape)
			);
			setSelectedCategory(
				props.materialRequest?.vendor_material?.type?.material_category?.id
			);
			setSelectedTypes([props.materialRequest?.vendor_material?.type?.id]);
			setSelectedHeight(Number(props.materialRequest.vendor_material.height));
			setSelectedWidth(Number(props.materialRequest.vendor_material.width));
			setSelectedOuterDiameter(
				props.materialRequest.vendor_material.outer_diameter
			);
			setSelectedInnerDiameter(
				props.materialRequest.vendor_material.inner_diameter
			);
			setSelectedWallThickness(
				props.materialRequest.vendor_material.wall_thickness
			);
			setPlateCutWidth(props.materialRequest.plate_cut_width);
			setMaterialCutLength(
				roundedCutLength ? String(roundedCutLength) : null
			);
			setTotalRequiredLength(
				Number(props.materialRequest.total_required_length)
			);
			setIsStockRotated(props.materialRequest.is_stock_rotated);
			setSawKerf(props.materialRequest.saw_kerf ?? (isSwissProduction ? SWISS_SAW_KERF : DEFAULT_SAW_KERF));
		setTrimCutLength(Number(props.materialRequest.required_trim_cut_length ?? DEFAULT_TRIM_CUT_LENGTH));

			if (props.materialRequest.is_stock_rotated) {
				setAllowRotatedStock(true);
			}

			if (props.materialRequest.vendor_material) {
				setSelectedRowKeys([props.materialRequest.vendor_material.id]);
				form.setFieldValue(
					'vendor_material',
					props.materialRequest.vendor_material.id
				);

				// Show the scrap recommendation hint as soon as the edit modal
				// opens: all data is already available, so the operator should
				// not have to re-click the already-selected vendor material to
				// see it (PTB-5965). updateField:false keeps the saved, possibly
				// user-edited, estimated_scrap_quantity untouched.
				if (!isSwissProduction) {
					const editShape = normalizeShape(
						props.materialRequest.vendor_material.shape
					);
					recomputeStandardScrap({
						quantity: props.materialRequest.material_cut_quantity,
						materialCutLength: roundedCutLength,
						shape: editShape?.display_name,
						height: props.materialRequest.vendor_material.height,
						width: props.materialRequest.vendor_material.width,
						outer_diameter:
							props.materialRequest.vendor_material.outer_diameter,
						inner_diameter:
							props.materialRequest.vendor_material.inner_diameter,
						wall_thickness:
							props.materialRequest.vendor_material.wall_thickness,
						materialTypeId:
							props.materialRequest.vendor_material.type?.id,
						updateField: false,
					});
				}
			}
		}
	}, [props.materialRequest, form, visible, isSwissProduction]);

	useEffect(() => {
		if (visible && !props.materialRequest && shapesQuery.isSuccess && !selectedShape) {
			if (isSwissProduction) {
				// Swiss defaults to Round Bar
				const roundBar = shapesQuery?.data?.find(
					(x) => x.display_name === ROUND_BAR
				);
				if (roundBar) {
					setSelectedShape(normalizeShape(roundBar));
					form.setFieldValue('shape', roundBar.id);
				}
			} else {
				// Other production methods default to Rectangle Bar
				const rectangleBar = shapesQuery?.data?.find(
					(x) => x.display_name === RECTANGLE_BAR
				);
				if (rectangleBar) {
					setSelectedShape(normalizeShape(rectangleBar));
					form.setFieldValue('shape', rectangleBar.id);
				}
			}
		}
	}, [visible, shapesQuery, isSwissProduction, props.materialRequest, selectedShape, form]);

	useEffect(() => {
		if (alignByFaceResult.isSuccess) {
			props.refetchLineItem();
			if (!stockPreviewQuery.isUninitialized) {
				stockPreviewQuery.refetch();
			}
			if (
				!getSuggestedVendorMaterialBasedOnPartOrientationQuery.isUninitialized
			) {
				getSuggestedVendorMaterialBasedOnPartOrientationQuery.refetch();
			}
		}
	}, [alignByFaceResult]);

	useEffect(() => {
		if (alignToXAxisByFaceResult.isSuccess) {
			props.refetchLineItem();
			if (!stockPreviewQuery.isUninitialized) {
				stockPreviewQuery.refetch();
			}
			if (
				!getSuggestedVendorMaterialBasedOnPartOrientationQuery.isUninitialized
			) {
				getSuggestedVendorMaterialBasedOnPartOrientationQuery.refetch();
			}
		}
	}, [alignToXAxisByFaceResult]);

	useEffect(() => {
		if (rotatePartResult.isSuccess) {
			props.refetchLineItem();
			if (!stockPreviewQuery.isUninitialized) {
				stockPreviewQuery.refetch();
			}
			if (
				!getSuggestedVendorMaterialBasedOnPartOrientationQuery.isUninitialized
			) {
				getSuggestedVendorMaterialBasedOnPartOrientationQuery.refetch();
			}
		}
	}, [rotatePartResult]);

	useEffect(() => {
		if (
			addMaterialRequestResult.isSuccess ||
			updateMaterialRequestResult.isSuccess
		) {
			form.resetFields();
			onClose();
		}
	}, [addMaterialRequestResult, updateMaterialRequestResult]);

	// Basic function that cheks the name of the material in the order line item
	// and selects material types that match a substring.
	useEffect(() => {
		if (!props.materialRequest && props.lineItem?.material_name && typesQuery.isSuccess) {
			const material = props.lineItem.material_name.toLowerCase();

			const types: string[] = [];
			const categories = new Set<string>();

			// Split "material" into tokens (words)
			const tokens = material
				.split(/\s+/) // split on whitespace
				.filter(Boolean); // remove empty strings

			for (const type of typesQuery.data) {
				const displayNameLower = type.display_name.toLowerCase();

				// Check if the displayName includes **all** tokens
				const hasAllTokens = tokens.every((token) =>
					displayNameLower.includes(token)
				);

				if (hasAllTokens) {
					types.push(type.id);
					if (type.material_category?.id) {
						categories.add(type.material_category.id);
					}
				}
			}

			// If there's exactly one category, set it
			if (categories.size === 1) {
				const category = Array.from(categories)[0];
				handleCategoryChange(category);
			}

			// Update types and query params
			setSelectedTypes([...types]);
		}
	}, [typesQuery]);

	useEffect(() => {
		if (
			getSuggestedVendorMaterialBasedOnPartOrientationQuery.isSuccess &&
			getSuggestedVendorMaterialBasedOnPartOrientationQuery?.data
				?.vendor_material &&
			(selectedShape?.display_name === RECTANGLE_BAR ||
				selectedShape?.display_name === ROUND_BAR)
		) {
			// Store auto-calculated value for Reset functionality (rounded to 2 decimals)
			const rawValue =
				getSuggestedVendorMaterialBasedOnPartOrientationQuery?.data
					?.material_cut_length;
			const autoCalculatedValue = rawValue
				? String(Number(Number(rawValue).toFixed(2)))
				: null;
			setAutoCalculatedPartLength(autoCalculatedValue);

			// Only auto-fill for new material requests
			if (!props.materialRequest) {
				onClickAutoSelectMaterialSize();
				setIsStockRotated(
					getSuggestedVendorMaterialBasedOnPartOrientationQuery?.data
						?.is_stock_rotated
				);
			}
		}
	}, [getSuggestedVendorMaterialBasedOnPartOrientationQuery]);

	// reset to first page whenever your filters change
	useEffect(() => {
		setCurrentPage(1);
	}, [
		selectedShape,
		selectedCategory,
		selectedTypes,
		selectedHeight,
		selectedWidth,
		selectedOuterDiameter,
		selectedInnerDiameter,
		selectedWallThickness,
	]);

	// Recompute the scrap recommendation when the selected material types change.
	// The cost per piece can only be resolved for a single known type
	// (getSelectedTypeId returns null for 0 or 2+ types), so switching between
	// one and many types must move between the lookup table and the smart
	// fallback. Without this, removing the extra type left the stale fallback
	// value on screen instead of recomputing the table value (PTB-5965). Skip
	// the first run (initial/auto-selected types are handled by the length/vendor
	// flows) and edit mode, so a saved, user-edited value is not overwritten.
	const typesScrapEffectInitialized = useRef(false);
	useEffect(() => {
		if (isSwissProduction || props.materialRequest) {
			return;
		}
		if (!typesScrapEffectInitialized.current) {
			typesScrapEffectInitialized.current = true;
			return;
		}
		recomputeStandardScrap({
			quantity: cutQuantity,
			materialCutLength,
			shape: selectedShape?.display_name,
			height: selectedHeight,
			width: selectedWidth,
			outer_diameter: selectedOuterDiameter,
			inner_diameter: selectedInnerDiameter,
			wall_thickness: selectedWallThickness,
			recalcLength: true,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedTypes]);

	// useEffect(() => {
	// 	if (vendorMaterialsQuery?.data?.count === 1) {
	// 		setSelectedRowKeys([vendorMaterialsQuery?.data?.results[0]?.id]);
	// 	}
	// }, [vendorMaterialsQuery]);

	// useEffect(() => {
	// 	if (selectedRowKeys.length > 0) {
	// 		form.setFieldValue('vendor_material', selectedRowKeys[0]);
	// 	}
	// }, [selectedRowKeys]);

	return (
		<Modal
			open={visible}
			title={
				props.materialRequest ? 'Update Material Request' : 'Request Material'
			}
			maskClosable={false}
			width={'calc(100vw - 50px)'}
			footer={
				<div className="flex flex-row justify-end gap-1">
					<Button onClick={handleCancel}>Close</Button>
					<Button
						type="primary"
						onClick={form.submit}
						loading={loading}
						disabled={!form.getFieldValue('vendor_material')}
					>
						Submit
					</Button>
				</div>
			}
			onCancel={handleCancel}
			destroyOnClose={true}
			confirmLoading={loading}
			centered={true}
			style={{
				maxWidth: '2560px',
			}}
			styles={{
				body: {
					height: 'calc(100vh - 150px)',
					maxHeight: '1080px',
					overflowX: 'hidden',
					overflowY: 'auto',
				},
			}}
		>
			<Splitter className="gap-2">
				<Splitter.Panel min={640}>
					<div className="h-full flex flex-col justify-between overflow-x-hidden">
						<Tabs
							defaultActiveKey={isSwissProduction ? '2' : '1'}
							items={[
								{
									label: '3D File',
									key: '1',
									children: (
										<>
											{!props.lineItem?.step_file ? (
												<>
													<Alert
														showIcon
														type="warning"
														description="A 3D file has not been uploaded. Stock Preview will not be enabled."
													/>
												</>
											) : (
												<Spin
													indicator={<LoadingOutlined />}
													tip="Generating Stock Preview"
													spinning={
														stockPreviewQuery.isLoading ||
														stockPreviewQuery.isFetching ||
														alignByFaceResult.isLoading ||
														alignToXAxisByFaceResult.isLoading ||
														rotatePartResult.isLoading
													}
												>
													<div
														// className="border rounded mb-3"
														style={{ height: '640px', overflow: 'hidden' }}
													>
														{stockPreviewQuery.isLoading ? (
															<LoadingOutlined />
														) : (
															<ThreeDStockSizePreviewer
																partBufferGeometry={
																	stockPreviewQuery?.data?.part
																}
																stockBufferGeometry={
																	stockPreviewQuery?.data?.stock
																}
																jawsBufferGeometry={
																	isSwissProduction
																		? undefined
																		: stockPreviewQuery?.data?.jaws
																}
																updateMatrix={updateMatrix}
																partMatrix={
																	props?.lineItem?.part_transform_matrix
																}
																showPartTransformControls={true}
																handleResetPartRotation={
																	handleResetPartRotation
																}
																handleClickFaceForAlignment={
																	handleClickFaceForAlignment
																}
																handleClickFaceForXAxisAlignment={
																	handleClickFaceForXAxisAlignment
																}
																loading={
																	stockPreviewQuery?.isLoading ||
																	stockPreviewQuery.isFetching
																}
															/>
														)}
													</div>
												</Spin>
											)}
										</>
									),
								},
								{
									label: 'Drawing File',
									key: '2',
									children: props?.lineItem?.drawing_file ? (
										<div
											style={{
												position: 'relative',
												width: '100%',
												height: 0,
												paddingBottom: '77%',
											}}
										>
											<iframe
												src={`${props?.lineItem?.drawing_file}#toolbar=1&scrollbar=1`}
												style={{
													position: 'absolute',
													width: '100%',
													height: '100%',
													left: 0,
													top: 0,
												}}
											/>
										</div>
									) : (
										<>
											<Alert
												showIcon
												type="warning"
												description="A Drawing file has not been uploaded."
											/>
										</>
									),
								},
							]}
						/>

						<Row gutter={[16, 16]}>
							<Col span={4}>
								<Text strong>Order</Text>
								<br />
								<Text>{props?.lineItem?.order.name}</Text>
							</Col>
							<Col span={6}>
								<Text strong>Part</Text>
								<br />
								<Text>{props?.lineItem?.name}</Text>
							</Col>
							<Col span={4}>
								<Text strong>Material</Text>
								<br />
								<Text>{props?.lineItem?.material_name}</Text>
							</Col>
							<Col span={4}>
								<Text strong>Quantity</Text>
								<br />
								<Text>{props?.lineItem?.quantity}</Text>
							</Col>
							<Col span={6}>
								<Text strong>Part Bounding Box</Text>
								{stockPreviewQuery.isLoading ? (
									<div className="flex flex-row gap-1">
										<LoadingOutlined /> Calculating
									</div>
								) : (
									<div>{`${stockPreviewQuery?.data?.part_bbox?.height}" H. X ${stockPreviewQuery?.data?.part_bbox?.width}" W. X ${stockPreviewQuery?.data?.part_bbox?.length}" L`}</div>
								)}
							</Col>
						</Row>
					</div>
				</Splitter.Panel>
				<Splitter.Panel min={480} style={{ overflowX: 'hidden' }}>
					<Form
						form={form}
						layout="vertical"
						onFinish={handleOk}
						requiredMark={false}
						style={{ paddingRight: '1rem' }}
						initialValues={{
							material_cut_length: null,
							material_cut_quantity: props?.lineItem?.quantity,
							estimated_scrap_quantity: isSwissProduction
								? 10 + Math.round(props?.lineItem?.quantity * 0.03)
								: Math.round(props?.lineItem?.quantity * 0.15),
							saw_kerf: isSwissProduction ? SWISS_SAW_KERF : DEFAULT_SAW_KERF,
							vendor_material: null,
							number_of_bars_needed: 0,
						}}
					>
				{buildings.length > 1 && (
					<Row gutter={[8, 0]}>
						<Col span={24}>
							<Form.Item
								name="building"
								label="Building"
								rules={[{ required: true, message: 'Please select a building' }]}
								initialValue={defaultBuildingId}
								help={lockedBuilding ? `This line item is locked to ${lockedBuilding.name}. All requests and reservations must be from the same building.` : undefined}
							>
								<Select
									placeholder="Select building"
									loading={buildingsLoading}
									disabled={!!lockedBuilding}
								>
									{buildings.map((b) => (
										<Select.Option key={b.id} value={b.id}>
											{b.name}
										</Select.Option>
									))}
								</Select>
							</Form.Item>
						</Col>
					</Row>
				)}
					<Row gutter={[8, 0]}>
						<Col span={12}>
							<Form.Item
								label="Shape"
								rules={[
									{ required: true, message: 'Please select a shape!' },
								]}
							>
								<Select
									placeholder="Select a shape"
									value={selectedShape?.id}
									allowClear={true}
									onClear={() => setSelectedShape(null)}
									onChange={handleShapeChange}
									options={shapesQuery?.data?.map((x) => {
										return { label: x.display_name, value: x.id };
									})}
								/>
							</Form.Item>
						</Col>

						<Col span={12}>
							<Form.Item
								label="Category"
								rules={[
									{ required: true, message: 'Please select a category!' },
								]}
							>
								<Select
									placeholder="Select a category"
									allowClear={true}
									onClear={() => setSelectedCategory(null)}
									options={categoriesQuery?.data?.map((x) => {
										return { label: x.display_name, value: x.id };
									})}
									value={selectedCategory}
									onChange={handleCategoryChange}
								/>
							</Form.Item>
						</Col>

							<Col span={24}>
								<Form.Item
									label="Types"
									rules={[{ required: true, message: 'Please select types!' }]}
								>
									<Select
										placeholder="Select types"
										showSearch={true}
										optionFilterProp="label"
										mode="multiple"
										value={selectedTypes}
										allowClear={true}
										onClear={() => setSelectedTypes([])}
										onChange={(value) => setSelectedTypes(value)}
										options={typesQuery?.data?.map((x) => {
											return { label: x.display_name, value: x.id };
										})}
										disabled={!selectedCategory}
									/>
								</Form.Item>
							</Col>

							{/* <Col span={12}>
								<Form.Item
									name="vendor"
									label="Vendor"
									rules={[
										{ required: true, message: 'Please select a vendor!' },
									]}
								>
									<Select
										placeholder="Select a vendor"
										allowClear={true}
										options={vendorsQuery?.data?.map((x) => {
											return { label: x.display_name, value: x.id };
										})}
									/>
								</Form.Item>
							</Col> */}
						</Row>
						<Row gutter={[8, 16]}>
							{selectedShape?.display_name === RECTANGLE_BAR ? (
								<Col span={24}>
									<Checkbox
										checked={allowRotatedStock}
										onChange={(e) => setAllowRotatedStock(e.target.checked)}
									>
										<div className="flex flex-row gap-1 items-center">
											Allow Rotated Stock
											<Tooltip title="Allow the stock to be rotated 90 degrees around the Y-axis. This increases the chance of finding a smaller and cheaper material but the stock orientation may not be suitable for robot loading.">
												<QuestionCircleTwoTone />
											</Tooltip>
										</div>
									</Checkbox>
								</Col>
							) : null}
							{selectedShape?.display_name === RECTANGLE_BAR ||
								selectedShape?.display_name === ROUND_BAR ? (
								<Col span={24}>
									<div>
										<div className="flex flex-col items-center mb-3">
											{getSuggestedVendorMaterialBasedOnPartOrientationQuery.isLoading ||
												getSuggestedVendorMaterialBasedOnPartOrientationQuery.isFetching ? (
												<div className="flex flex-row gap-2">
													<LoadingOutlined /> Searching the vendor material
													database for the best size
												</div>
											) : (
												<>
													{getSuggestedVendorMaterialBasedOnPartOrientationQuery.isSuccess ? (
														<>
															{getSuggestedVendorMaterialBasedOnPartOrientationQuery
																?.data?.vendor_material ? (
																<>
																	<div className="flex flex-col items-center gap-2">
																		<Text italic>
																			{selectedShape?.display_name ===
																				RECTANGLE_BAR
																				? `Suggested Size: ${getSuggestedVendorMaterialBasedOnPartOrientationQuery?.data?.vendor_material?.height}" H. X ${getSuggestedVendorMaterialBasedOnPartOrientationQuery?.data?.vendor_material?.width}" W. X ${getSuggestedVendorMaterialBasedOnPartOrientationQuery?.data?.material_cut_length}" ${isSwissProduction ? 'Part Length' : 'Cut Length'}`
																				: selectedShape?.display_name ===
																					ROUND_BAR
																					? `Suggested Size: ${getSuggestedVendorMaterialBasedOnPartOrientationQuery?.data?.vendor_material?.outer_diameter}" OD. X ${getSuggestedVendorMaterialBasedOnPartOrientationQuery?.data?.material_cut_length}" ${isSwissProduction ? 'Part Length' : 'Cut Length'}`
																					: null}
																		</Text>
																		{getSuggestedVendorMaterialBasedOnPartOrientationQuery
																			?.data?.is_stock_rotated ? (
																			<Tag
																				color="blue"
																				icon={<WarningOutlined />}
																			>
																				Stock is rotated
																			</Tag>
																		) : null}
																	</div>
																</>
															) : (
																<>No matching vendor material found</>
															)}
														</>
													) : null}
												</>
											)}
										</div>
									</div>
								</Col>
							) : null}

							<Form.Item name="is_stock_rotated" hidden={true} />

							{(selectedShape?.display_name === RECTANGLE_BAR ||
								selectedShape?.display_name === RECTANGLE_TUBE ||
								selectedShape?.display_name === PLATE ||
								selectedShape?.display_name === ANGLE ||
								selectedShape?.display_name === CHANNEL ||
								selectedShape?.display_name === HEX_BAR) && (
									<Col span={8}>
										<Form.Item
											label="Height"
											rules={[
												{
													required: true,
													message: 'Please input the height!',
												},
											]}
										>
											<Select
												placeholder="Select a height"
												showSearch={true}
												optionFilterProp="label"
												value={selectedHeight}
												allowClear={true}
												onClear={() => {
													setSelectedHeight(null);
													setSelectedRowKeys([]);
													form.resetFields(['vendor_material']);
												}}
												onChange={(value) => {
													setSelectedHeight(value);
													setSelectedRowKeys([]);
													form.resetFields(['vendor_material']);
												}}
												options={uniqueHeightsQuery?.data?.map((x) => {
													return {
														value: x,
														label: x,
													};
												})}
												disabled={!selectedCategory}
											/>
										</Form.Item>
									</Col>
								)}

							{(selectedShape?.display_name === RECTANGLE_BAR ||
								selectedShape?.display_name === RECTANGLE_TUBE ||
								selectedShape?.display_name === ANGLE ||
								selectedShape?.display_name === CHANNEL) && (
									<Col span={8}>
										<Form.Item
											label="Width"
											rules={[
												{
													required: true,
													message: 'Please input the width!',
												},
											]}
										>
											<Select
												placeholder="Select a width"
												showSearch={true}
												optionFilterProp="label"
												value={selectedWidth}
												allowClear={true}
												onClear={() => {
													setSelectedWidth(null);
													setSelectedRowKeys([]);
													form.resetFields(['vendor_material']);
												}}
												onChange={(value) => {
													setSelectedWidth(value);
													setSelectedRowKeys([]);
													form.resetFields(['vendor_material']);
												}}
												options={uniqueWidthsQuery?.data?.map((x) => {
													return { value: x, label: x };
												})}
												disabled={!selectedCategory}
												loading={
													uniqueWidthsQuery.isLoading ||
													uniqueWidthsQuery.isFetching
												}
											/>
										</Form.Item>
									</Col>
								)}

							{selectedShape?.display_name === PLATE && (
								<Col span={8}>
									<Form.Item
										name="plate_cut_width"
										label="Cut Width"
										rules={[
											{
												required: true,
												message: 'Please input the width!',
											},
										]}
									>
										<InputNumber
											placeholder="Cut Width"
											value={plateCutWidth}
											step={0.25}
											onStep={(value) => handlePlateCutWidthChange(value)}
											onBlur={(e) => handlePlateCutWidthChange(e.target.value)}
											style={{ width: '100%' }}
											suffix="in"
										/>
									</Form.Item>
								</Col>
							)}

							{(selectedShape?.display_name === RECTANGLE_TUBE ||
								selectedShape?.display_name === ANGLE ||
								selectedShape?.display_name === CHANNEL) && (
									<Col span={8}>
										<Form.Item
											name="wall_thickness"
											label="Wall Thickness"
											rules={[
												{
													required: true,
													message: 'Please input the wall thickness!',
												},
											]}
										>
											<Select
												placeholder="Select a wall thickness"
												showSearch={true}
												optionFilterProp="label"
												value={selectedWallThickness}
												onChange={(value) => setSelectedWallThickness(value)}
												options={uniqueWallThicknessesQuery?.data?.map((x) => {
													return { value: x, label: x };
												})}
												disabled={!selectedCategory}
											/>
										</Form.Item>
									</Col>
								)}

							{selectedShape?.display_name === ROUND_BAR ||
								selectedShape?.display_name === ROUND_TUBE ? (
								<Col span={8}>
									<Form.Item
										label="Outer Diameter"
										rules={[
											{
												required: true,
												message: 'Please input the outer diameter!',
											},
										]}
									>
										<Select
											placeholder="Select a diameter"
											showSearch={true}
											optionFilterProp="label"
											value={selectedOuterDiameter}
											allowClear={true}
											onClear={() => {
												setSelectedOuterDiameter(null);
												setSelectedRowKeys([]);
												form.resetFields(['vendor_material']);
											}}
											onChange={(value) => {
												setSelectedOuterDiameter(value);
												setSelectedRowKeys([]);
												form.resetFields(['vendor_material']);
											}}
											options={uniqueOuterDiametersQuery?.data?.map((x) => {
												return {
													value: x,
													label: x,
												};
											})}
											disabled={!selectedCategory}
										/>
									</Form.Item>
								</Col>
							) : null}

							{selectedShape?.display_name === ROUND_TUBE && (
								<Col span={8}>
									<Form.Item
										name={'inner_diameter'}
										label="Inner Diameter"
										rules={[
											{
												required: true,
												message: 'Please input the inner diameter!',
											},
										]}
									>
										<Select
											placeholder="Select an inner diameter"
											showSearch={true}
											optionFilterProp="label"
											value={selectedInnerDiameter}
											onChange={(value) => setSelectedInnerDiameter(value)}
											options={uniqueInnerDiametersQuery?.data?.map((x) => {
												return {
													value: x,
													label: x,
												};
											})}
											disabled={!selectedCategory}
										/>
									</Form.Item>
								</Col>
							)}
							<Col span={8}>
								<Form.Item
									name="material_cut_length"
									label={
										<div className="flex items-center gap-2">
											<span>{isSwissProduction ? 'Part Length' : 'Cut Length'}</span>
											{isSwissProduction && autoCalculatedPartLength && (
												<Button
													type="link"
													size="small"
													onClick={handleResetPartLength}
													disabled={
														materialCutLength === autoCalculatedPartLength ||
														Number(materialCutLength) ===
														Number(autoCalculatedPartLength)
													}
													style={{
														padding: 0,
														height: 'auto',
														fontSize: '14px',
													}}
												>
													Reset
												</Button>
											)}
										</div>
									}
									rules={[
										{
											required: true,
											message: 'Please input the length!',
										},
									]}
								>
									<InputNumber
										placeholder={isSwissProduction ? 'Part Length' : 'Cut Length'}
										value={materialCutLength}
										step={0.125}
										precision={2}
										onChange={(value) => handleMaterialCutLengthChange(value)}
										onBlur={(e) => {
											const val = e.target.value;
											if (val) {
												const roundedValue = String(Number(Number(val).toFixed(2)));
												handleMaterialCutLengthChange(roundedValue);
											} else {
												handleMaterialCutLengthChange(null);
											}
										}}
										style={{ width: '100%' }}
										suffix="in"
									/>
								</Form.Item>
							</Col>
						</Row>

						<Row gutter={[8, 0]} style={{ marginBottom: 16 }}>
						<Col span={8}>
							<Form.Item
								name="saw_kerf"
								label={isSwissProduction ? 'Cut Off Length' : 'Saw Kerf'}
								initialValue={sawKerf}
								rules={[
									{
										required: true,
										message: isSwissProduction
											? 'Please input the cut off length!'
											: 'Please input the saw kerf!',
									},
								]}
							>
								<InputNumber
									placeholder={
										isSwissProduction ? 'Cut Off Length' : 'Saw Kerf'
									}
									step={0.01}
									min={0}
									value={sawKerf}
									onStep={(value) => handleSawKerfChange(value)}
									onBlur={(e) => handleSawKerfChange(Number(e.target.value))}
									style={{ width: '100%' }}
								/>
							</Form.Item>
						</Col>
						{!isSwissProduction && (
							<Col span={8}>
								<Form.Item
									name="required_trim_cut_length"
									label="Trim Cut"
									initialValue={trimCutLength}
								>
									<InputNumber
										placeholder="Trim Cut"
										step={0.1}
										precision={3}
										min={0}
										value={trimCutLength}
										onStep={(value) => {
											setTrimCutLength(value);
											if (materialCutLength) {
												calculateRequiredLengths({
													material_cut_length: Number(materialCutLength),
													saw_kerf: sawKerf,
													material_cut_quantity: estimatedScrapQuantity + cutQuantity,
													required_trim_cut_length: value,
												}).unwrap().then((data) => {
													setTotalRequiredLength(data.total_required_length);
													form.setFieldValue('total_required_length', data.total_required_length);
												});
											}
										}}
										onBlur={(e) => {
											const value = Number(e.target.value);
											setTrimCutLength(value);
											if (materialCutLength) {
												calculateRequiredLengths({
													material_cut_length: Number(materialCutLength),
													saw_kerf: sawKerf,
													material_cut_quantity: estimatedScrapQuantity + cutQuantity,
													required_trim_cut_length: value,
												}).unwrap().then((data) => {
													setTotalRequiredLength(data.total_required_length);
													form.setFieldValue('total_required_length', data.total_required_length);
												});
											}
										}}
										style={{ width: '100%' }}
									/>
								</Form.Item>
							</Col>
						)}
							<Col span={8}>
								<Form.Item
									name="material_cut_quantity"
									label={isSwissProduction ? 'Parts' : 'Cut Quantity'}
									rules={[
										{
											required: true,
											message: isSwissProduction
												? 'Please input the parts!'
												: 'Please input the cut quantity!',
										},
									]}
								>
									<InputNumber
										placeholder={isSwissProduction ? 'Parts' : 'Cut Quantity'}
										step={1}
										min={1}
										value={cutQuantity}
										onStep={(value) => handleMaterialCutQuantityChange(value)}
										onBlur={(e) =>
											handleMaterialCutQuantityChange(Number(e.target.value))
										}
										style={{ width: '100%' }}
									/>
								</Form.Item>
							</Col>
							<Col span={8}>
								<Form.Item
									name="estimated_scrap_quantity"
									label="Estimated Scrap Quantity"
									rules={[
										{
											required: true,
											message: 'Please input the scrap quantity!',
										},
									]}
									style={{ marginBottom: 0 }}
								>
									<InputNumber
										placeholder="Estimated Scrap Quantity"
										step={1}
										min={0}
										value={estimatedScrapQuantity}
										onStep={(value) => handleMaterialScrapQuantityChange(value)}
										onBlur={(e) =>
											handleMaterialScrapQuantityChange(Number(e.target.value))
										}
										style={{ width: '100%' }}
									/>
								</Form.Item>
								{!isSwissProduction && (
									<ScrapRecommendation
										breakdown={scrapBreakdown}
										onApply={handleMaterialScrapQuantityChange}
									/>
								)}
							</Col>

							<Col span={24}>
								<Form.Item name="total_required_length" hidden={true} />

								{isSwissProduction ? (
									<>
										{totalRequiredLength !== 0 ? (
											<div className="flex flex-col items-end mb-3">
												<Title level={5}>
													Total Inches Needed:{' '}
													<span className="text-lg p-1 rounded">
														{`${totalRequiredLength.toFixed(2)}"`}
													</span>
												</Title>
											</div>
										) : null}
									</>
								) : (
									totalRequiredLength !== 0 ? (
										<div className="flex flex-col items-end mb-5">
											<Title level={5}>
												Total Required Length:{' '}
												<span className="text-lg p-1 rounded">
													{`${totalRequiredLength}"`}
												</span>
											</Title>
										</div>
									) : null
								)}
							</Col>
						</Row>

						{isSwissProduction && (
							<Row gutter={[8, 0]}>
								<Col span={8}>
									<Form.Item
										name="number_of_bars_needed"
										label="Number of Bars Needed"
										rules={[
											{
												required: true,
												message: 'Please input the number of bars!',
											},
										]}
									>
										<InputNumber
											placeholder="Number of Bars Needed"
											step={1}
											min={1}
											value={numberOfBars}
											onChange={(value) => {
												if (value !== null) {
													setNumberOfBars(value);
													form.setFieldValue(
														'number_of_bars_needed',
														value
													);
												}
											}}
											style={{ width: '100%' }}
										/>
									</Form.Item>
								</Col>
							</Row>
						)}

						{vendorMaterialsQuery?.isSuccess ? (
							<>
								<div className="flex items-baseline gap-1">
									<Title level={5}>Select a Vendor Material</Title>
									<Tooltip title="This is the material you are requesting for this part">
										<QuestionCircleTwoTone />
									</Tooltip>
								</div>
								<Table
									size="small"
									className="mb-3"
									loading={vendorMaterialsQuery.isLoading}
									columns={[
										{
											title: 'Vendor',
											render: (record) => record.vendor.display_name,
										},
										{
											title: 'Part Number',
											render: (record) => (
												<Tooltip
													title={
														record.vendor_description
															? record.vendor_description
															: null
													}
												>
													{record.vendor_part_number}
												</Tooltip>
											),
										},
										{
											title: 'Type',
											render: (record) => record.type.display_name,
										},
									{
										title: 'Size',
										render: (record) => computeVendorMaterialSize(record),
									},
									]}
									pagination={{
										current: currentPage,
										pageSize,
										total: vendorMaterialsQuery.data.count,
										onChange: (page, size) => {
											setCurrentPage(page);
											setPageSize(size);
										},
									}}
									dataSource={vendorMaterialsQuery?.data?.results}
									rowKey={'id'}
									rowSelection={{
										type: 'radio',
										selectedRowKeys: selectedRowKeys,
										onChange: onSelectedRowKeysChange,
									}}
									onRow={(record) => ({
										style: {
											cursor: 'pointer',
										},
										onClick: () => {
											selectRow(record);
										},
									})}
									locale={{
										emptyText: (
											<Empty description="No vendor materials found that match your search" />
										),
									}}
								/>
							</>
						) : null}

						<Button
							type="dashed"
							block={true}
							className="mb-5"
							icon={<PlusOutlined />}
							onClick={() => dispatch(showAddVendorMaterialModal())}
						>
							Add New Vendor Material
						</Button>

						{/* {selectedVendorMaterial ? (
							<Col span={8}>
								<Form.Item
									name="vendor_material_length"
									label="Full Bar Length"
									rules={[
										{
											required: true,
											message: 'Please select a bar length',
										},
									]}
								>
									<Select
										placeholder="Select a bar length"
										value={selectedInnerDiameter}
										onChange={(value) => setSelectedInnerDiameter(value)}
										options={selectedVendorMaterial?.lengths.map((length) => {
											return {
												value: length.id,
												label: `${Number(length.length)} inches`,
											};
										})}
										disabled={!selectedCategory}
									/>
								</Form.Item>
							</Col>
						) : null} */}

						<VendorMaterialModal
							refetchData={() => {
								vendorMaterialsQuery.refetch();
								if (
									selectedShape?.display_name == RECTANGLE_BAR ||
									selectedShape?.display_name == HEX_BAR ||
									selectedShape?.display_name == PLATE ||
									selectedShape?.display_name == RECTANGLE_TUBE ||
									selectedShape?.display_name == ANGLE ||
									selectedShape?.display_name == CHANNEL
								) {
									uniqueHeightsQuery.refetch();
								} else if (
									selectedShape?.display_name == ROUND_BAR ||
									selectedShape?.display_name == ROUND_TUBE
								) {
									uniqueOuterDiametersQuery.refetch();
								}
							}}
						/>

						{/* <Radio.Group value={null}>
							{vendorMaterialsQuery?.data?.results?.map((x) => (
								<Radio key={x.id} className="mb-2">
									{x.vendor_description}
								</Radio>
							))}
						</Radio.Group> */}

						{/* Vendor Part Number */}
						{/* <Form.Item name="vendor_part_number" label="Vendor Part Number">
							<Input placeholder="Vendor Part Number" />
						</Form.Item> */}

						{/* <Form.Item name="vendor_description" label="Vendor Description">
							<Input.TextArea placeholder="Vendor Description" />
						</Form.Item> */}

						<Form.Item name="vendor_material" hidden={true}>
							<Input />
						</Form.Item>

						<Form.Item name="notes" label="Notes">
							<Input.TextArea placeholder="Notes" />
						</Form.Item>
					</Form>
				</Splitter.Panel>
			</Splitter>
		</Modal>
	);
};

export default MaterialRequestModal;
